import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, dealsAPI, ticketsAPI, leadsAPI, tasksAPI, activitiesAPI, emailIntegrationAPI, meetingsAPIExtended } from '../api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { UniversalSearch } from '../components/UniversalSearch';
import { CustomerPortalActivityPanel } from '../components/CustomerPortalActivityPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format, formatDistanceToNow, isToday, isTomorrow, isPast, addDays } from 'date-fns';
import {
  Users, Building, HandshakeIcon, Target, TicketIcon, Megaphone,
  DollarSign, TrendingUp, Clock, AlertCircle, CheckSquare, Calendar,
  Activity, Bell, ChevronRight, User, Mail, Phone, MessageSquare,
  CreditCard, FileText, Video, Search, Inbox, Send, ArrowDownLeft, ArrowUpRight,
  RefreshCw, Loader2, Edit, ExternalLink, Plus
} from 'lucide-react';
import { CompanyAnnouncementsSection, CompanyActivityFeed, CustomAppLinks } from '../components/CompanyDashboard';

export const DashboardPage = () => {
  const { user, hasRole } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [recentDeals, setRecentDeals] = useState([]);
  const [openTickets, setOpenTickets] = useState([]);
  const [newLeads, setNewLeads] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [recentEmails, setRecentEmails] = useState([]);
  const [emailSyncStatus, setEmailSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Quick Add Task state
  const [quickTaskOpen, setQuickTaskOpen] = useState(false);
  const [quickTaskLoading, setQuickTaskLoading] = useState(false);
  const [quickTaskForm, setQuickTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      const [statsRes, dealsRes, ticketsRes, leadsRes, tasksRes, meetingsRes, activitiesRes, emailsRes, syncStatusRes] = await Promise.all([
        dashboardAPI.getStats(),
        hasRole(['admin', 'sales_rep']) ? dealsAPI.getAll() : Promise.resolve({ data: [] }),
        hasRole(['admin', 'support_agent', 'technical_support', 'customer']) ? ticketsAPI.getAll({ status: 'open' }) : Promise.resolve({ data: [] }),
        hasRole(['admin', 'sales_rep', 'marketing_manager']) ? leadsAPI.getAll({ status: 'new' }) : Promise.resolve({ data: [] }),
        tasksAPI.getUpcoming().catch(() => ({ data: [] })),
        meetingsAPIExtended.getUpcoming().catch(() => ({ data: [] })),
        activitiesAPI.getRecent(20).catch(() => ({ data: [] })),
        emailIntegrationAPI.getAllEmails({ limit: 10 }).catch(() => ({ data: [] })),
        emailIntegrationAPI.getSyncStatus().catch(() => ({ data: null }))
      ]);

      setStats(statsRes.data);
      setRecentDeals((dealsRes.data || []).slice(0, 5));
      setOpenTickets((ticketsRes.data || []).slice(0, 5));
      setNewLeads((leadsRes.data || []).slice(0, 5));
      // API returns array directly for these endpoints
      setUpcomingTasks(Array.isArray(tasksRes.data) ? tasksRes.data : (Array.isArray(tasksRes) ? tasksRes : []));
      setUpcomingMeetings(Array.isArray(meetingsRes.data) ? meetingsRes.data : (Array.isArray(meetingsRes) ? meetingsRes : []));
      setRecentActivities(Array.isArray(activitiesRes.data) ? activitiesRes.data : (Array.isArray(activitiesRes) ? activitiesRes : []));
      setRecentEmails(Array.isArray(emailsRes.data?.emails) ? emailsRes.data.emails : (Array.isArray(emailsRes.data) ? emailsRes.data : []));
      setEmailSyncStatus(syncStatusRes.data);
      
      if (!loading) {
        toast.success('Dashboard refreshed');
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to refresh dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleQuickTaskSubmit = async (e) => {
    e.preventDefault();
    if (!quickTaskForm.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    
    try {
      setQuickTaskLoading(true);
      await tasksAPI.create({
        title: quickTaskForm.title,
        description: quickTaskForm.description,
        priority: quickTaskForm.priority,
        due_date: quickTaskForm.due_date,
        status: 'pending',
        assigned_to: user?.id
      });
      
      toast.success('Task created successfully!');
      setQuickTaskOpen(false);
      setQuickTaskForm({
        title: '',
        description: '',
        priority: 'medium',
        due_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
      });
      
      // Refresh dashboard to show new task
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    } finally {
      setQuickTaskLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const getTaskDueLabel = (dueDate) => {
    if (!dueDate) return null;
    try {
      const date = new Date(dueDate);
      if (isNaN(date.getTime())) return null;
      if (isPast(date)) return { label: 'Overdue', color: 'bg-red-100 text-red-700' };
      if (isToday(date)) return { label: 'Today', color: 'bg-amber-100 text-amber-700' };
      if (isTomorrow(date)) return { label: 'Tomorrow', color: 'bg-blue-100 text-blue-700' };
      return { label: format(date, 'MMM d'), color: 'bg-slate-100 text-slate-600' };
    } catch (e) {
      return null;
    }
  };

  const getActivityIcon = (type) => {
    const icons = {
      note: MessageSquare,
      email: Mail,
      call: Phone,
      meeting: Video,
      task: CheckSquare,
      deal: HandshakeIcon,
      ticket: TicketIcon,
      payment: CreditCard,
      contact: User,
      lead: Target,
      create: FileText,
      update: FileText,
      delete: FileText
    };
    return icons[type] || Activity;
  };

  const statCards = [
    { label: 'Total Contacts', value: stats?.total_contacts || 0, icon: Users, color: 'text-blue-600', href: '/contacts' },
    { label: 'Companies', value: stats?.total_companies || 0, icon: Building, color: 'text-slate-600', href: '/companies' },
    { label: 'Active Deals', value: stats?.total_deals || 0, icon: HandshakeIcon, color: 'text-emerald-600', href: '/deals' },
    { label: 'New Leads', value: stats?.total_leads || 0, icon: Target, color: 'text-amber-600', href: '/leads' },
    { label: 'Open Tickets', value: stats?.open_tickets || 0, icon: TicketIcon, color: 'text-red-600', href: '/tickets' },
    { label: 'Active Campaigns', value: stats?.active_campaigns || 0, icon: Megaphone, color: 'text-purple-600', href: '/campaigns' },
  ];

  const stageColors = {
    prospecting: 'bg-slate-100 text-slate-700',
    qualification: 'bg-blue-100 text-blue-700',
    proposal: 'bg-amber-100 text-amber-700',
    negotiation: 'bg-purple-100 text-purple-700',
    closed_won: 'bg-emerald-100 text-emerald-700',
    closed_lost: 'bg-red-100 text-red-700'
  };

  const priorityColors = {
    low: 'bg-slate-100 text-slate-600',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-orange-100 text-orange-700',
    urgent: 'bg-red-100 text-red-700'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back, {user?.full_name || user?.email}!</p>
        </div>
        <div className="flex items-center gap-3">
          <UniversalSearch className="w-80" />
          
          {/* Quick Add Task Button */}
          <Dialog open={quickTaskOpen} onOpenChange={setQuickTaskOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1" data-testid="quick-add-task-btn">
                <Plus className="h-4 w-4" />
                Quick Task
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckSquare className="h-5 w-5 text-amber-600" />
                  Create Quick Task
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleQuickTaskSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="task-title">Task Title *</Label>
                  <Input
                    id="task-title"
                    placeholder="Enter task title..."
                    value={quickTaskForm.title}
                    onChange={(e) => setQuickTaskForm(p => ({ ...p, title: e.target.value }))}
                    data-testid="quick-task-title-input"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="task-description">Description (Optional)</Label>
                  <Textarea
                    id="task-description"
                    placeholder="Add details..."
                    value={quickTaskForm.description}
                    onChange={(e) => setQuickTaskForm(p => ({ ...p, description: e.target.value }))}
                    rows={2}
                    data-testid="quick-task-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="task-priority">Priority</Label>
                    <Select
                      value={quickTaskForm.priority}
                      onValueChange={(v) => setQuickTaskForm(p => ({ ...p, priority: v }))}
                    >
                      <SelectTrigger data-testid="quick-task-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="task-due-date">Due Date</Label>
                    <Input
                      id="task-due-date"
                      type="date"
                      value={quickTaskForm.due_date}
                      onChange={(e) => setQuickTaskForm(p => ({ ...p, due_date: e.target.value }))}
                      data-testid="quick-task-due-date-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setQuickTaskOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={quickTaskLoading} data-testid="quick-task-submit-btn">
                    {quickTaskLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                    Create Task
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          
          <Button variant="outline" size="sm" onClick={fetchDashboardData} disabled={refreshing} data-testid="refresh-dashboard-btn">
            {refreshing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <Link key={index} to={stat.href}>
            <Card className="card-hover cursor-pointer transition-all hover:shadow-md" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground label-uppercase">{stat.label}</p>
                    <p className="text-2xl font-bold font-['Manrope'] mt-1">{stat.value}</p>
                  </div>
                  <stat.icon className={`h-8 w-8 ${stat.color}`} strokeWidth={1.5} />
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Customer Portal Activity - unified staff view of portal submissions */}
      <CustomerPortalActivityPanel />

      {/* Company Dashboard Section */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <CompanyAnnouncementsSection />
        </div>
        <div className="space-y-6">
          <CustomAppLinks />
          <CompanyActivityFeed />
        </div>
      </div>

      {/* Action Items Row - Upcoming Tasks & Meetings */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upcoming Tasks */}
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CheckSquare className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
                Action Items
              </CardTitle>
              <Link to="/tasks">
                <Button variant="ghost" size="sm">
                  View all <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
            <CardDescription>Tasks requiring your attention</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              {upcomingTasks.length > 0 ? (
                <div className="space-y-3">
                  {upcomingTasks.slice(0, 6).map((task) => {
                    const dueInfo = getTaskDueLabel(task.due_date);
                    return (
                      <Link key={task.id} to="/tasks" className="block">
                        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group">
                          <CheckSquare className={`h-4 w-4 mt-0.5 ${task.status === 'completed' ? 'text-emerald-600' : 'text-slate-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium text-sm ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {task.title}
                            </p>
                            {task.contact && (
                              <p className="text-xs text-blue-600 mt-0.5 flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {task.contact.first_name} {task.contact.last_name}
                                {task.contact.email && <span className="text-slate-400">• {task.contact.email}</span>}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">{task.priority}</Badge>
                              {dueInfo && <Badge className={`text-xs ${dueInfo.color}`}>{dueInfo.label}</Badge>}
                              {task.assignee && (
                                <span className="text-xs text-muted-foreground">• {task.assignee.full_name || task.assignee.email}</span>
                              )}
                            </div>
                          </div>
                          <Edit className="h-4 w-4 text-slate-300 group-hover:text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckSquare className="h-12 w-12 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No upcoming tasks</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Upcoming Meetings */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                Upcoming Meetings
              </CardTitle>
              <Button variant="ghost" size="sm">
                View all <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            <CardDescription>Scheduled meetings this week</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              {upcomingMeetings.length > 0 ? (
                <div className="space-y-3">
                  {upcomingMeetings.slice(0, 6).map((meeting) => {
                    let meetingTime = 'Scheduled';
                    let meetingDate = '';
                    try {
                      if (meeting.start_time) {
                        const date = new Date(meeting.start_time);
                        if (!isNaN(date.getTime())) {
                          meetingTime = format(date, 'h:mm a');
                          meetingDate = format(date, 'EEE, MMM d');
                        }
                      }
                    } catch (e) {
                      meetingTime = 'Scheduled';
                    }
                    return (
                      <div key={meeting.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="bg-blue-100 rounded-lg p-2 text-center min-w-[60px]">
                          <p className="text-xs text-blue-600 font-medium">{meetingDate || 'TBD'}</p>
                          <p className="text-sm font-bold text-blue-700">{meetingTime}</p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{meeting.title}</p>
                          {meeting.contact && (
                            <Link 
                              to={`/contacts/${meeting.contact.id}`} 
                              className="text-xs text-blue-600 hover:underline mt-0.5 flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <User className="h-3 w-3" />
                              {meeting.contact.first_name} {meeting.contact.last_name}
                              {meeting.contact.phone && <span className="text-slate-400">• {meeting.contact.phone}</span>}
                            </Link>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            {meeting.meeting_link && (
                              <a 
                                href={meeting.meeting_link} 
                                target="_blank" 
                                rel="noreferrer" 
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <Video className="h-3 w-3" />
                                Join meeting
                              </a>
                            )}
                            {meeting.location && !meeting.meeting_link && (
                              <span className="text-xs text-muted-foreground">{meeting.location}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No upcoming meetings</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Cards */}
      {hasRole(['admin', 'sales_rep']) && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="card-hover">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground label-uppercase">
                Total Pipeline Value
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <DollarSign className="h-10 w-10 text-emerald-600" strokeWidth={1.5} />
                <div>
                  <p className="text-3xl font-bold font-['Manrope']">{formatCurrency(stats?.deals_value)}</p>
                  <p className="text-sm text-muted-foreground">Across all deal stages</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground label-uppercase">
                Won Deals Value
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <TrendingUp className="h-10 w-10 text-blue-600" strokeWidth={1.5} />
                <div>
                  <p className="text-3xl font-bold font-['Manrope']">{formatCurrency(stats?.won_deals_value)}</p>
                  <p className="text-sm text-muted-foreground">Closed won deals</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Activity Monitor & Recent Items */}
      <div className="grid lg:grid-cols-4 gap-6">
        {/* Activity Monitor */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity className="h-5 w-5 text-emerald-600" strokeWidth={1.5} />
              Activity Monitor
            </CardTitle>
            <CardDescription>Recent system activity</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[350px]">
              {recentActivities.length > 0 ? (
                <div className="space-y-3">
                  {recentActivities.map((activity, index) => {
                    const Icon = getActivityIcon(activity.type || activity.action);
                    let timeAgo = 'recently';
                    try {
                      if (activity.created_at) {
                        const date = new Date(activity.created_at);
                        if (!isNaN(date.getTime())) {
                          timeAgo = formatDistanceToNow(date, { addSuffix: true });
                        }
                      }
                    } catch (e) {
                      timeAgo = 'recently';
                    }
                    return (
                      <div key={activity.id || index} className="flex items-start gap-2 py-2 border-b last:border-0">
                        <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-3 w-3 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs leading-tight">
                            <span className="font-medium">{activity.author?.full_name || activity.author?.email || 'System'}</span>
                            {' '}{activity.action || activity.type}{' '}
                            <span className="text-muted-foreground">{activity.entity_type}</span>
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {timeAgo}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No recent activity</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Recent Deals */}
        {hasRole(['admin', 'sales_rep']) && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <HandshakeIcon className="h-5 w-5" strokeWidth={1.5} />
                  Recent Deals
                </CardTitle>
                <Link to="/deals">
                  <Button variant="ghost" size="sm">View all</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[320px]">
                {recentDeals.length > 0 ? (
                  <div className="space-y-3">
                    {recentDeals.map((deal) => (
                      <Link key={deal.id} to={`/deals`} className="block">
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                          <div>
                            <p className="font-medium text-sm">{deal.title}</p>
                            <p className="text-xs text-muted-foreground">{formatCurrency(deal.value)}</p>
                          </div>
                          <Badge className={stageColors[deal.stage]}>
                            {deal.stage.replace('_', ' ')}
                          </Badge>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No deals yet</p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Open Tickets */}
        {hasRole(['admin', 'support_agent', 'technical_support', 'customer']) && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <TicketIcon className="h-5 w-5" strokeWidth={1.5} />
                  Open Tickets
                </CardTitle>
                <Link to="/tickets">
                  <Button variant="ghost" size="sm">View all</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[320px]">
                {openTickets.length > 0 ? (
                  <div className="space-y-3">
                    {openTickets.map((ticket) => (
                      <Link key={ticket.id} to={`/tickets`} className="block">
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{ticket.subject}</p>
                            <p className="text-xs text-muted-foreground">{ticket.type}</p>
                          </div>
                          <Badge className={priorityColors[ticket.priority]}>
                            {ticket.priority}
                          </Badge>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No open tickets</p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* New Leads */}
        {hasRole(['admin', 'sales_rep', 'marketing_manager']) && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" strokeWidth={1.5} />
                  New Leads
                </CardTitle>
                <Link to="/leads">
                  <Button variant="ghost" size="sm">View all</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[320px]">
                {newLeads.length > 0 ? (
                  <div className="space-y-3">
                    {newLeads.map((lead) => (
                      <Link key={lead.id} to={`/leads`} className="block">
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                          <div>
                            <p className="font-medium text-sm">{lead.first_name} {lead.last_name}</p>
                            <p className="text-xs text-muted-foreground">{lead.company_name || lead.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">Score: {lead.score}</Badge>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No new leads</p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Email Activity Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Inbox className="h-5 w-5" strokeWidth={1.5} />
                Email Activity
              </CardTitle>
              <Link to="/email-settings">
                <Button variant="ghost" size="sm">Settings</Button>
              </Link>
            </div>
            {emailSyncStatus?.connected && (
              <CardDescription className="flex items-center gap-2 text-xs">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                {emailSyncStatus.email_count} emails synced
                {emailSyncStatus.last_sync_at && ` · Last sync ${formatDistanceToNow(new Date(emailSyncStatus.last_sync_at), { addSuffix: true })}`}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {!emailSyncStatus?.connected ? (
              <div className="text-center py-8">
                <Mail className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-muted-foreground mb-3">Connect your Outlook to sync emails</p>
                <Link to="/email-settings">
                  <Button variant="outline" size="sm">
                    Connect Email
                  </Button>
                </Link>
              </div>
            ) : (
              <ScrollArea className="h-[280px]">
                {recentEmails.length > 0 ? (
                  <div className="space-y-3">
                    {recentEmails.slice(0, 8).map((email) => (
                      <Link key={email.id} to={email.contact_id ? `/contacts/${email.contact_id}` : '#'} className="block">
                        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                          <div className={`mt-1 p-1.5 rounded-full ${email.direction === 'inbound' ? 'bg-blue-100' : 'bg-green-100'}`}>
                            {email.direction === 'inbound' ? (
                              <ArrowDownLeft className="h-3 w-3 text-blue-600" />
                            ) : (
                              <ArrowUpRight className="h-3 w-3 text-green-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{email.subject || '(No subject)'}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {email.direction === 'inbound' ? `From: ${email.from_email}` : `To: ${email.to_email}`}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              {email.sent_at ? formatDistanceToNow(new Date(email.sent_at), { addSuffix: true }) : 'Recently'}
                            </p>
                          </div>
                          {email.contact_id && (
                            <Badge variant="outline" className="text-xs shrink-0">
                              Linked
                            </Badge>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-muted-foreground">No emails synced yet</p>
                    <p className="text-xs text-slate-400 mt-1">Emails sync automatically every 5 minutes</p>
                  </div>
                )}
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
